(() => {
  const responsiveImageSources = (src, nativeWidth) => {
    const width = Number(nativeWidth);
    if (!src || !Number.isSafeInteger(width) || width < 1) return '';
    try {
      const url = new URL(src, document.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      return [...new Set([330, 352, 480, 600, 660, 750, 832, 990, 1200, 1440, 1680, 1920, 2400, width])]
        .filter(candidate => candidate <= width).sort((left, right) => left - right)
        .map(candidate => {
          url.searchParams.set('width', String(candidate));
          return `${url.href} ${candidate}w`;
        }).join(', ');
    } catch {
      return '';
    }
  };
  const optionIds = root => Array.from(root?.querySelectorAll('variant-selects select option:checked, variant-selects input:checked') || [])
    .map(option => String(option.dataset.optionValueId || '')).filter(Boolean).sort();
  const sameOptions = (left, right) => left.length === right.length && left.every((value, index) => value === right[index]);

  class FuguiVariantPanel extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.info = this.closest('product-info');
      this.expectedOptions = optionIds(this.info);
      this.onClick = this.onClick.bind(this);
      this.onError = () => { clearTimeout(this.timeout); this.showState('error'); };
      this.onUnavailable = () => { clearTimeout(this.timeout); this.showState('unavailable'); };
      this.addEventListener('click', this.onClick);
      this.info?.addEventListener('fugui:variant-fetch-error', this.onError);
      this.info?.addEventListener('fugui:variant-unavailable', this.onUnavailable);
      this.unsubscribers = [];
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        if (!data?.event?.target || !this.info?.contains(data.event.target)) return;
        this.lastSelection = data;
        this.expectedOptions = data.selectedOptionValues.map(String).sort();
        this.showState('updating');
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => this.showState('error'), 15000);
      }));
      this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.applyVariant(data)));
    }

    disconnectedCallback() {
      this.stopVideo();
      this.connected = false;
      clearTimeout(this.timeout);
      this.unsubscribers?.forEach(unsubscribe => unsubscribe());
      this.removeEventListener('click', this.onClick);
      this.info?.removeEventListener('fugui:variant-fetch-error', this.onError);
      this.info?.removeEventListener('fugui:variant-unavailable', this.onUnavailable);
    }

    applyVariant(data) {
      if (String(data?.sectionId) !== String(this.dataset.section)) return;
      const source = Array.from(data.html?.querySelectorAll(this.localName) || [])
        .find(panel => String(panel.dataset.section) === String(this.dataset.section));
      const sourceInfo = source?.closest('product-info');
      const formVariant = this.info?.querySelector('product-form input[name="id"]')?.value;
      if (!source || String(source.dataset.productId) !== String(this.dataset.productId) ||
        String(sourceInfo?.dataset.productId) !== String(this.dataset.productId) ||
        String(source.dataset.variantId) !== String(data.variant?.id) ||
        String(formVariant) !== String(data.variant?.id) ||
        !sameOptions(optionIds(sourceInfo), this.expectedOptions)) return;
      clearTimeout(this.timeout);
      this.stopVideo();
      this.innerHTML = source.innerHTML;
      this.dataset.variantId = source.dataset.variantId;
      if (source.dataset.primaryId !== undefined) this.dataset.primaryId = source.dataset.primaryId;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-fg-state');
      this.dispatchEvent(new CustomEvent('fugui:variant-panel-ready', {
        bubbles: true,
        detail: { variantId: this.dataset.variantId, primaryId: this.dataset.primaryId }
      }));
    }

    showState(state) {
      this.stopVideo();
      this.setAttribute('aria-busy', state === 'updating' ? 'true' : 'false');
      this.dataset.fgState = state;
      const feedback = this.querySelector('[data-fg-feedback]');
      if (!feedback) return;
      feedback.hidden = false;
      const text = feedback.querySelector('[data-fg-feedback-text]');
      if (text) text.textContent = this.dataset[state] || '';
      const retry = feedback.querySelector('[data-fg-retry]');
      if (retry) retry.hidden = state !== 'error' || !this.lastSelection;
    }

    stopVideo() {
      if (this.localName !== 'fg-approved-gallery') return;
      const stage = this.querySelector('[data-fg-video-stage]');
      stage?.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      stage?.replaceChildren();
      if (stage) stage.hidden = true;
      this.querySelector('.fg-reviewed-gallery__hero')?.removeAttribute('data-fg-active-video');
      this.querySelector('.fg-reviewed-gallery__image')?.removeAttribute('aria-hidden');
    }

    showVideo(button, image, figure) {
      if (this.localName !== 'fg-approved-gallery') return false;
      if (figure.hasAttribute('data-fg-active-video') && figure.dataset.fgMediaId === button.dataset.fgMediaId) return true;
      const template = Array.from(this.querySelectorAll('[data-fg-video-template]'))
        .find(item => item.dataset.fgMediaId === button.dataset.fgMediaId);
      const stage = this.querySelector('[data-fg-video-stage]');
      if (!template || !stage) return false;
      this.stopVideo();
      stage.append(template.content.cloneNode(true));
      stage.hidden = false;
      figure.setAttribute('data-fg-active-video', '');
      image.setAttribute('aria-hidden', 'true');
      const video = stage.querySelector('video');
      if (video) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.preload = 'metadata';
        video.load();
        video.setAttribute('aria-label', button.dataset.fgAlt || button.dataset.fgCaption || '');
        video.tabIndex = 0;
        video.focus({ preventScroll: true });
      }
      return true;
    }

    onClick(event) {
      if (event.target.closest('[data-fg-retry]') && this.lastSelection) {
        this.showState('updating');
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => this.showState('error'), 15000);
        this.info?.handleOptionValueChange({ data: this.lastSelection });
        return;
      }
      const detailsLink = event.target.closest('[data-fg-details-link]');
      if (detailsLink && this.contains(detailsLink)) {
        const details = document.getElementById(detailsLink.hash.slice(1));
        if (details) details.open = true;
      }
      const button = event.target.closest('[data-fg-thumbnail]');
      if (!button || !this.contains(button) || this.hasAttribute('data-fg-state')) return;
      const image = this.querySelector('.fg-reviewed-gallery__image');
      const figure = this.querySelector('.fg-reviewed-gallery__hero');
      if (!image || !figure) return;
      const isVideo = button.dataset.fgMediaType === 'video';
      if (isVideo) {
        if (!this.showVideo(button, image, figure)) return;
      } else {
        this.stopVideo();
        const srcset = responsiveImageSources(button.dataset.fgSrc, button.dataset.fgWidth);
        if (srcset) image.srcset = srcset;
        else image.removeAttribute('srcset');
        image.src = button.dataset.fgSrc;
        image.alt = button.dataset.fgAlt || '';
        image.width = Number(button.dataset.fgWidth);
        image.height = Number(button.dataset.fgHeight);
        figure.style.setProperty('--fg-native-width', `${button.dataset.fgWidth}px`);
      }
      figure.dataset.fgMediaId = button.dataset.fgMediaId;
      const caption = this.querySelector('[data-fg-gallery-caption]');
      if (caption) caption.textContent = button.dataset.fgCaption || image.alt;
      this.querySelectorAll('[data-fg-thumbnail]').forEach(item => item.removeAttribute('aria-current'));
      button.setAttribute('aria-current', 'true');
      const status = this.querySelector('[data-fg-gallery-status]');
      const announcement = isVideo ? this.dataset.videoAnnouncement : this.dataset.announcement;
      if (status) status.textContent = (announcement || '').replace('__index__', button.dataset.fgIndex);
    }
  }

  if (!customElements.get('fg-approved-gallery')) customElements.define('fg-approved-gallery', class extends FuguiVariantPanel {});
  if (!customElements.get('fg-variant-specs')) customElements.define('fg-variant-specs', class extends FuguiVariantPanel {});
  if (!customElements.get('fg-product-essentials')) customElements.define('fg-product-essentials', class extends FuguiVariantPanel {});
})();


